import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, Download, Power } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSheetsSyncWidget() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStatus();
    loadConfig();
  }, []);

  const loadStatus = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/sync-status`);
      if (r.ok) {
        const data = await r.json();
        setStatus(data);
      }
    } catch (e) {
      console.error('Failed to load sync status:', e);
    }
  };

  const loadConfig = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/config`);
      if (r.ok) {
        const data = await r.json();
        setConfig(data);
      }
    } catch (e) {
      console.error('Failed to load config:', e);
    }
  };

  const toggleAutoSync = async () => {
    setLoading(true);
    try {
      const newEnabled = !config?.auto_sync_enabled;
      const r = await fetch(`${BACKEND_URL}/api/sheets/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, auto_sync_enabled: newEnabled }),
      });
      if (!r.ok) throw new Error('Failed to update config');
      
      await loadConfig();
      toast.success(`Auto-sync ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(`Failed to toggle auto-sync: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runBackfill = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/sync-now`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error('Backfill failed');
      
      await loadStatus();
      toast.success('Backfill completed successfully');
    } catch (e) {
      toast.error(`Backfill failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return '—';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  const metrics = status?.metrics || {};
  const autoSyncEnabled = config?.auto_sync_enabled || false;

  return (
    <Card className="glass-card" data-testid="sheets-sync-widget">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Google Sheets Lead Sync</CardTitle>
          <div className="flex items-center gap-2">
            <div className={`text-xs px-2 py-1 rounded-full ${
              autoSyncEnabled
                ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600'
                : 'bg-slate-700/50 text-slate-400 border border-slate-600'
            }`}>
              {autoSyncEnabled ? 'Auto-Sync ON' : 'Auto-Sync OFF'}
            </div>
          </div>
        </div>
        {status?.has_synced && (
          <div className="text-xs text-slate-400 mt-1">
            Last run: {formatDate(status.last_sync)} • Duration: {formatDuration(status.duration)}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Metrics Grid */}
        {status?.has_synced ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="New Leads" value={metrics.new_leads || 0} color="cyan" />
            <MetricCard label="Updated" value={metrics.updated_leads || 0} color="purple" />
            <MetricCard label="Skipped" value={metrics.skipped_dedup || 0} color="slate" />
            <MetricCard label="Errors" value={metrics.errors || 0} color={metrics.errors > 0 ? 'rose' : 'emerald'} />
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div className="text-sm">No sync has been performed yet</div>
            <div className="text-xs mt-1">Configure Google Sheets in Settings to begin</div>
          </div>
        )}

        {/* Additional Stats */}
        {status?.has_synced && (
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <div className="text-slate-400">Total Processed</div>
              <div className="text-lg font-semibold mt-1">{metrics.total_rows || 0}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <div className="text-slate-400">Unchanged Rows</div>
              <div className="text-lg font-semibold mt-1">{metrics.skipped_unchanged || 0}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            onClick={runBackfill}
            disabled={loading}
            className="bg-gold from-gold to-ink-700"
            data-testid="backfill-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Run Backfill Now
          </Button>
          <Button
            onClick={toggleAutoSync}
            disabled={loading}
            variant="outline"
            data-testid="toggle-auto-sync-btn"
          >
            <Power className="w-4 h-4 mr-2" />
            {autoSyncEnabled ? 'Disable' : 'Enable'} Auto-Sync
          </Button>
          {metrics.errors > 0 && (
            <Button
              as="a"
              href={`${BACKEND_URL}/api/sheets/sync-status?format=csv`}
              variant="secondary"
              data-testid="download-errors-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Failed Rows
            </Button>
          )}
        </div>

        {/* Configuration Summary */}
        {config && (
          <div className="pt-3 border-t border-slate-700 text-xs text-slate-400 space-y-1">
            <div><strong>Spreadsheet:</strong> {config.spreadsheet_id ? `${config.spreadsheet_id.slice(0, 20)}...` : 'Not configured'}</div>
            <div><strong>Worksheet:</strong> {config.worksheet_name || 'Sheet1'}</div>
            <div><strong>Sync Schedule:</strong> Every hour at :{String(config.cron_minute || 5).padStart(2, '0')}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCard({ label, value, color = 'slate' }) {
  const colorClasses = {
    cyan: 'bg-gold border-gold/40 text-gold',
    purple: 'bg-ink-700 border-ink-200 dark:border-ink-800 text-ink-500',
    slate: 'bg-slate-800/50 border-slate-600 text-slate-300',
    emerald: 'bg-emerald-900/20 border-emerald-600 text-emerald-400',
    rose: 'bg-rose-900/20 border-rose-600 text-rose-400',
  };

  return (
    <div className={`rounded-lg p-3 border ${colorClasses[color] || colorClasses.slate}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-70 mt-1">{label}</div>
    </div>
  );
}
